import { useQueries } from "@tanstack/react-query";
import { getAtkomstNodeUsers } from "@coworker/app/src/hooks/API/core.service";
import { INTERVAL } from "@coworker/app/src/constants/clientTime";

type AtkomstNodeUser = {
  email: string;
  roles: string[];
};

const NODE_USERS_STALE_TIME = 5 * INTERVAL.MINUTE;

type MergedAtkomstUser = {
  email: string;
  roles: string[];
  nodeRefs: string[];
};

export const useAtkomstNodeUsersMulti = (nodeRefs: string[]) => {
  const queries = useQueries({
    queries: nodeRefs.map((nodeRef) => ({
      queryKey: ["atkomstNodeUsers", nodeRef],
      queryFn: () => getAtkomstNodeUsers(nodeRef) as Promise<AtkomstNodeUser[]>,
      staleTime: NODE_USERS_STALE_TIME,
      refetchOnWindowFocus: false,
      retry: false,
    })),
  });

  const isLoading = queries.some((q) => q.isLoading);
  const isError = queries.some((q) => q.isError);
  const isFetching = queries.some((q) => q.isFetching);

  const byEmail = new Map<string, MergedAtkomstUser>();
  queries.forEach((q, index) => {
    const nodeRef = nodeRefs[index];
    if (!nodeRef || !q.data) return;
    q.data.forEach((user) => {
      const existing = byEmail.get(user.email);
      if (existing) {
        existing.roles = Array.from(
          new Set([...existing.roles, ...user.roles])
        );
        if (!existing.nodeRefs.includes(nodeRef)) {
          existing.nodeRefs.push(nodeRef);
        }
      } else {
        byEmail.set(user.email, {
          email: user.email,
          roles: [...user.roles],
          nodeRefs: [nodeRef],
        });
      }
    });
  });

  return {
    data: Array.from(byEmail.values()),
    isLoading,
    isError,
    isFetching,
  };
};
